import { ArrowRight, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LanguageSwitcher } from '@nx/i18n';
import { cn } from './cn';

export interface TopNavCategory {
  name: string;
  path: string;
}

export interface TopNavProps {
  onLogin: () => void;
  categories?: TopNavCategory[];
  signupPath?: string;
  loginLabel?: string;
  signupLabel?: string;
}

const DEFAULT_CATEGORIES: TopNavCategory[] = [
  { name: 'Corporate', path: '/corporate' },
  { name: 'Experiences', path: '/experiences' },
  { name: 'Locations', path: '/locations' },
  { name: 'Impact', path: '/impact' },
  { name: 'Partners', path: '/partners' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

export function TopNav({
  onLogin,
  categories = DEFAULT_CATEGORIES,
  signupPath = '/signup',
  loginLabel = 'Login',
  signupLabel = 'Sign up',
}: TopNavProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActiveCategory = (path: string) => location.pathname === path;
  const handleLogoClick = () => navigate('/');

  return (
    <nav className="bg-white/95 backdrop-blur-md border-b border-[#3D3328]/10 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
        <div className="flex items-center justify-between">
          <div className="flex-shrink-0">
            <h1
              onClick={handleLogoClick}
              className="text-xl md:text-2xl tracking-tight text-[#1A1A1A] cursor-pointer font-extrabold"
              style={{ fontFamily: 'var(--font-sans)' }}
            >
              N‑Excelsior
            </h1>
          </div>

          <div className="hidden lg:flex items-center gap-8 flex-1 justify-center">
            {categories.map((category) => (
              <Link
                key={category.path}
                to={category.path}
                className={cn(
                  'text-sm font-semibold transition-colors relative group',
                  isActiveCategory(category.path)
                    ? 'text-[#1A1A1A]'
                    : 'text-[#3D3328] hover:text-[#1A1A1A]',
                )}
              >
                {category.name}
                <span
                  className={cn(
                    'absolute bottom-0 left-0 h-0.5 bg-[#1A1A1A] transition-all duration-300',
                    isActiveCategory(category.path) ? 'w-full' : 'w-0 group-hover:w-full',
                  )}
                />
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            <LanguageSwitcher className="hidden md:inline-flex" />
            <button
              onClick={onLogin}
              className="hidden md:block text-sm text-[#78665A] hover:text-[#3D3328] transition-colors"
            >
              {loginLabel}
            </button>
            <Link
              to={signupPath}
              className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-[#1A1A1A] hover:bg-black text-[#F4D03F] transition-all shadow-md hover:shadow-lg hover:scale-105 transform duration-200 text-sm font-bold rounded-xl"
            >
              {signupLabel}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={signupPath}
              className="md:hidden px-4 py-2 bg-[#1A1A1A] text-[#F4D03F] rounded-xl text-sm font-bold flex items-center justify-center"
            >
              {signupLabel}
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              className="lg:hidden p-2 text-[#3D3328] hover:text-[#1A1A1A]"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-[#3D3328]/10 pt-4">
            <div className="flex flex-col gap-4">
              {categories.map((category) => (
                <Link
                  key={category.path}
                  to={category.path}
                  className={cn(
                    'text-left text-sm font-semibold transition-colors py-2',
                    isActiveCategory(category.path)
                      ? 'text-[#1A1A1A]'
                      : 'text-[#3D3328] hover:text-[#1A1A1A]',
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {category.name}
                </Link>
              ))}
              <button
                onClick={() => {
                  onLogin();
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-sm text-[#78665A] hover:text-[#3D3328] transition-colors py-2"
              >
                {loginLabel}
              </button>
              <LanguageSwitcher className="self-start" />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
